import { getErrorMessage } from '@federa/utils/errors/error-core';
import { HTTP_STATUS } from '@federa/const/http-status';
import { API_TIMEOUTS } from '@federa/const/timing';
import { buildBearerJsonPostInit, buildJsonPostInit } from '@federa/const/http-request-init';
import { asPlainObjectOrEmpty } from '@federa/const/record-guards';
import { readStoredGuestSessionToken } from '@federa/utils/auth/guest-session-token-storage';
import { extractStringOrNull } from '@federa/utils/validation/extract-string-field';
import { getFirebaseIdToken, getFirebaseUser } from './fetch-helpers.ts';
import { fetchTracedEdge } from './traced-edge-fetch.ts';
import { resolveEnvelopeSuccess } from './api-envelope.ts';
import type { StandardResult } from '@federa/types/api-response';

/** POST JSON to an internal API route and return a StandardResult (build request, fetch, parse JSON, extract result fields). */
export async function postJsonRoute(route: string, data: Record<string, unknown>): Promise<StandardResult> {
	return postJsonRouteWithInit(route, buildJsonPostInit(data));
}

/**
 * Authenticated sibling of {@link postJsonRoute} for USER-SCOPED routes that verify the caller's Firebase ID token --
 * the payments-payouts wallet surface, whose entry gate fail-closes on a missing credential, and the app's own
 * `/api/internal/issues/create` door, which additionally names the recorded actor from the verified subject.
 *
 * A SEPARATE entry point rather than a change to `postJsonRoute`: that helper serves non-user-scoped callers too, and
 * attaching a credential to all of them would widen where the token travels for no gate that reads it.
 *
 * The token is resolved PER CALL, never captured once -- a Firebase ID token expires in ~1h and `getIdToken()`
 * refreshes it transparently, so a hoisted constant starts 401ing partway through a session. Same discipline as
 * `mfe/wishlists/frontend/src/wishlist-api.ts`.
 *
 * A signed-out caller yields NO `Authorization` header (`buildBearerJsonPostInit` sends none for a null token) rather than a thrown error:
 * the DECIDING belongs to the worker's gate, which answers 401, and sending the headerless request keeps ONE authority
 * for that decision instead of a drifting client-side copy. The payments UI prompts login ahead of these surfaces, so
 * a signed-out caller reaching here is already off the happy path.
 */
export async function postJsonRouteAuthed(route: string, data: Record<string, unknown>): Promise<StandardResult> {
	const token = await getFirebaseIdToken();
	return postJsonRouteWithInit(route, buildBearerJsonPostInit(data, token));
}

/**
 * Credential resolver for surfaces a GUEST may legitimately reach: the signed-in user's Firebase ID token, else the
 * chats guest session token, else null.
 *
 * Firebase FIRST and never both: an authenticated visitor can still carry a stale guest token in localStorage from a
 * signed-out session, and presenting that instead would bind the request to the guest subject -- the authed user's own
 * rows would read as someone else's.
 */
async function resolveUserOrGuestCredential(): Promise<string | null> {
	const user = await getFirebaseUser();
	if (user) return user.getIdToken();
	return readStoredGuestSessionToken();
}

/**
 * Sibling of {@link postJsonRouteAuthed} for routes whose gate accepts EITHER credential the chat surface issues --
 * the chats flow-dispatch mount, which verifies a Firebase ID token or a signed guest session token and binds the
 * caller from whichever proved out.
 *
 * A THIRD entry point rather than a widened `postJsonRouteAuthed`: that helper is Firebase-only by contract, and a
 * guest token on the payments/issue routes it serves would present a subject those gates must never accept.
 *
 * Resolved PER CALL for the reason `postJsonRouteAuthed` documents, and additionally because a guest's token does not
 * exist until the bootstrap create returns it -- a hoisted read taken at mount is null for the whole session.
 *
 * A caller with neither credential sends NO `Authorization` header rather than throwing: the worker's gate owns the
 * verdict, and duplicating it here would be a second copy to drift.
 */
export async function postJsonRouteUserOrGuest(route: string, data: Record<string, unknown>): Promise<StandardResult> {
	const token = await resolveUserOrGuestCredential();
	return postJsonRouteWithInit(route, buildBearerJsonPostInit(data, token));
}

/**
 * Shared transport + envelope parse; the POST variants differ ONLY in the `init` they hand in.
 *
 * Over `fetchTracedEdge`, so every internal-route POST carries the originating-edge `traceparent` and the worker run
 * behind the route joins the click that filed it. LONG-RUNNING rather than the standard 10s: the routes served here
 * include the payouts/refund money surface, and a cap tighter than the work would turn a slow settlement into a
 * client-side failure on a request the server is still processing. The credential and the envelope contract are
 * unchanged -- the three public entry points above still decide which token rides, and a non-ok response still returns
 * a `StandardResult` rather than throwing.
 */
async function postJsonRouteWithInit(route: string, init: RequestInit): Promise<StandardResult> {
	try {
		const response = await fetchTracedEdge(route, { ...init, timeout: API_TIMEOUTS.longRunning });

		// `Response.json()` resolves as `any`; the narrowing below is what decides the shape, so the binding starts `unknown`.
		const rawBody: unknown = await response.json();
		const body = asPlainObjectOrEmpty(rawBody);
		// success comes from the body tag, else the HTTP status. The tag-or-status rule lives in resolveEnvelopeSuccess -- it READS the tag instead of casting.
		const success = resolveEnvelopeSuccess(rawBody, response.status);
		// `message` THEN `error`, because the two arms of this union spell their text under different keys and reading
		// only the first discarded every failure's own reason. `error` is the key EVERY failure envelope in this repo
		// binds -- `buildAdapterError`, `mfeJsonError`, `createValidationFailedStep` -- and it is the key
		// `parseApiEnvelope` reads for exactly that reason. Before this, a `{ success: false, error: 'Payout below
		// minimum' }` reached the caller as `message: undefined` and every UI rendered its own generic placeholder over
		// a message the server had already written. `message` keeps precedence: the adapter error envelope mirrors its
		// text into BOTH keys, so the order only decides which of two identical strings wins there, while a success
		// envelope carrying a real `message` is never overridden by a stray `error`.
		const message = extractStringOrNull(body, 'message') ?? extractStringOrNull(body, 'error') ?? undefined;
		// Constructed per-arm: StandardResult is literal-tagged, so a single `success` boolean cannot be assigned into the union.
		if (success) return { success: true, status: response.status, message, raw: body };
		return { success: false, status: response.status, message, raw: body };
	} catch (err) {
		// No log record: the same failure reaches the caller in the returned StandardResult. `raw: undefined` is explicit -- the transport type requires one runtime key set.
		return {
			success: false,
			status: HTTP_STATUS.INTERNAL_SERVER_ERROR,
			message: `POST ${route} failed: ${getErrorMessage(err)}`,
			raw: undefined,
		};
	}
}
